import type { MeasurementKind } from '../columnUnits.ts';
import { type Column, col, type Shape } from './base.ts';

export const IDENTITY: Shape = [
  col('externalId', 'unitless', 'Instance external id; prefer for fast id-based traversal.'),
  col('space', 'unitless', 'Instance space.'),
];

/**
 * The producing-string relation column, parameterized by its source suffix —
 * base solution-model views name the relation `entityName` where the Agent
 * views use `wellString`; the canonical agent-facing name stays `wellString`.
 */
export const stringRelation = (suffix?: string) =>
  col('wellString', 'unitless', 'Producing string (completion) this row belongs to.', suffix ? { suffix } : undefined);

/** Producing-string relation carried by every per-string view. */
export const STRING_RELATION: Shape = [stringRelation()];
/**
 * Identity + the producing-string relation: the passthrough every per-string
 * view keeps unrenamed, so the agent can scope by hierarchy (filter on
 * wellString, join WellEntity) or enrich ancestry from it.
 */
export const STRING_KEEP: Shape = [...IDENTITY, ...STRING_RELATION];

/** Oil/gas/water production rates — reused across estimated, network, and rollup views. */
export const PHASE_RATES: Shape = [
  col('oilRate', 'liquidRate', 'Oil production rate.', { suffix: 'OilRate' }),
  col('gasRate', 'gasRate', 'Gas production rate.', { suffix: 'GasRate' }),
  col('waterRate', 'liquidRate', 'Water production rate.', { suffix: 'WaterRate' }),
];

/** Well/completion setpoints and pressures an optimization scenario solves for. */
export const SETPOINTS: Shape = [
  col('chokeBeanSize', 'chokeSize', 'Choke bean size setpoint.', { suffix: 'ChokeBeanSize' }),
  col('chokeOpening', 'fraction', 'Choke opening setpoint.', { suffix: 'ChokeOpening' }),
  col('espFrequency', 'frequency', 'ESP frequency setpoint.', { suffix: 'EspFrequency' }),
  col('fbhp', 'pressureAbsolute', 'Flowing bottom-hole pressure.', { suffix: 'FBHP' }),
  col('fwhp', 'pressureAbsolute', 'Flowing well-head pressure.', { suffix: 'FWHP' }),
  col('gasLiftRate', 'gasRate', 'Gas-lift injection rate.', { suffix: 'GasLiftRate' }),
  col('power', 'power', 'Power draw.', { suffix: 'Power' }),
];

/** The run identifier every optimization-scenario family carries, on every grain. */
export const SCENARIO_RUN_ID: Shape = [
  col('runId', 'unitless', 'Optimization run identifier.', { suffix: 'RunID', valueType: 'string' }),
];

/**
 * Operator annotations carried by the ad-hoc scenario families (string + field
 * grains). Resolved against the scenario prefix (e.g. `adHocSimulation1`), so the
 * `suffix` is the scenario-local tail (`Comment`/`ExecutedBy`); both are
 * string-valued annotation timeseries, so each carries `valueType: 'string'`.
 */
export const ADHOC_ANNOTATIONS: Shape = [
  col('comment', 'unitless', 'Free-text comment on the ad-hoc run.', { suffix: 'Comment', valueType: 'string' }),
  col('executedBy', 'unitless', 'User who executed the ad-hoc run.', { suffix: 'ExecutedBy', valueType: 'string' }),
];

/**
 * The base optimization-scenario measures shared by EVERY grain (string, field,
 * reservoir): the oil/gas/water phase rates plus the run identifier. Each grain
 * extends this with its own setpoints / extras / annotations.
 */
export const SCENARIO_BASE: Shape = [...PHASE_RATES, ...SCENARIO_RUN_ID];

/** A full optimization-scenario result: the shared base + string-grain setpoints + run metadata. */
export const OPTIMIZATION_RESULT: Shape = [
  ...SCENARIO_BASE,
  ...SETPOINTS,
  col('optimizedParameter', 'unitless', 'Control parameter the scenario optimized.', {
    suffix: 'OptimizedParameter',
    valueType: 'string',
  }),
];

/** An ad-hoc scenario result: the standard result plus operator annotations. */
export const AD_HOC_RESULT: Shape = [...OPTIMIZATION_RESULT, ...ADHOC_ANNOTATIONS];

/** Artificial-lift pump (ESP/pump) operating outputs. */
export const PUMP_OUTPUTS: Shape = [
  col('pumpEfficiency', 'fraction', 'Pump hydraulic efficiency.', { suffix: 'PumpEfficiency' }),
  col('pumpHead', 'length', 'Pump developed head.', { suffix: 'PumpHead' }),
  col('pumpIntakePressure', 'pressureAbsolute', 'Pump intake pressure.', { suffix: 'PumpIntakePress' }),
  col('pumpOutletPressure', 'pressureAbsolute', 'Pump discharge (outlet) pressure.', { suffix: 'PumpOutletPress' }),
  col('pumpPower', 'power', 'Pump power draw.', { suffix: 'PumpPower' }),
  col('pumpRate', 'liquidRate', 'Pump throughput (volumetric) rate.', { suffix: 'PumpRate' }),
];

/** Electrical motor / drive operating outputs for the artificial-lift system. */
export const MOTOR_OUTPUTS: Shape = [
  col('motorCurrent', 'current', 'Motor line current.', { suffix: 'MotorCurrent' }),
  col('motorEfficiency', 'fraction', 'Motor electrical efficiency.', { suffix: 'MotorEfficiency' }),
  col('motorLoad', 'fraction', 'Motor load fraction.', { suffix: 'MotorLoad' }),
  col('motorPower', 'power', 'Motor power draw.', { suffix: 'MotorPower' }),
  col('motorRpm', 'unitless', 'Motor rotational speed.', { suffix: 'MotorRPM' }),
  col('surfaceKva', 'apparentPower', 'Surface apparent power (kVA).', { suffix: 'SurfaceKVA' }),
];

/** Gas-lift injection design and operating outputs. */
export const GAS_LIFT_OUTPUTS: Shape = [
  col('chp', 'pressureAbsolute', 'Gas-lift casing-head pressure.', { suffix: 'GlChp' }),
  col('injectionDepth', 'length', 'Gas-lift injection (valve) depth.', { suffix: 'GlInjectionDepth' }),
  col('orificeDiameter', 'chokeSize', 'Gas-lift orifice/valve port diameter.', { suffix: 'GlOrificeDiameter' }),
  col('valveCasingPressure', 'pressureAbsolute', 'Gas-lift valve casing-side pressure.', {
    suffix: 'GlValveCasingPress',
  }),
  col('valveTubingPressure', 'pressureAbsolute', 'Gas-lift valve tubing-side pressure.', {
    suffix: 'GlValveTubingPress',
  }),
  col('glr', 'gasOilRatio', 'Produced gas-liquid ratio.', { suffix: 'Glr' }),
  col('glrInjected', 'gasOilRatio', 'Injected gas-liquid ratio.', { suffix: 'GlrInjected' }),
];

/**
 * Per-phase solver match: measured vs estimated vs difference for each
 * production phase. The phase→suffix mapping is the SSOT for the
 * `WellSolver*` axis; encoded here because the oil phase is irregular —
 * its difference is the bare `WellSolverDifference` column, not a
 * `WellSolverDifferenceOilRate` (which does not exist on the source).
 */
const WELL_SOLVER_PHASES: readonly {
  readonly phase: string;
  readonly rateKind: MeasurementKind;
  readonly measuredSuffix: string;
  readonly estimatedSuffix: string;
  readonly differenceSuffix: string;
}[] = [
  {
    phase: 'oil',
    rateKind: 'liquidRate',
    measuredSuffix: 'WellSolverMeasuredOilRate',
    estimatedSuffix: 'WellSolverEstimatedOilRate',
    differenceSuffix: 'WellSolverDifference',
  },
  {
    phase: 'gas',
    rateKind: 'gasRateModel',
    measuredSuffix: 'WellSolverMeasuredGasRate',
    estimatedSuffix: 'WellSolverEstimatedGasRate',
    differenceSuffix: 'WellSolverDifferenceGasRate',
  },
  {
    phase: 'water',
    rateKind: 'liquidRate',
    measuredSuffix: 'WellSolverMeasuredWaterRate',
    estimatedSuffix: 'WellSolverEstimatedWaterRate',
    differenceSuffix: 'WellSolverDifferenceWaterRate',
  },
  {
    phase: 'liquid',
    rateKind: 'liquidRate',
    measuredSuffix: 'WellSolverMeasuredLiquidRate',
    estimatedSuffix: 'WellSolverEstimatedLiquidRate',
    differenceSuffix: 'WellSolverDifferenceLiquidRate',
  },
];

/**
 * Well-solver match diagnostics: flattened measured/estimated/difference per
 * production phase, plus the solver's matched pressures, GOR and water cut.
 * The producing-string solver's quality of fit against measured conditions.
 */
export const WELL_SOLVER_MATCH: Shape = [
  ...WELL_SOLVER_PHASES.flatMap((p: (typeof WELL_SOLVER_PHASES)[number]): readonly Column[] => [
    col(`${p.phase}Measured`, p.rateKind, `Solver measured ${p.phase} rate.`, { suffix: p.measuredSuffix }),
    col(`${p.phase}Estimated`, p.rateKind, `Solver estimated ${p.phase} rate.`, { suffix: p.estimatedSuffix }),
    col(`${p.phase}Difference`, p.rateKind, `Solver measured-vs-estimated ${p.phase} difference.`, {
      suffix: p.differenceSuffix,
    }),
  ]),
  col('fbhp', 'pressureAbsolute', 'Solver-matched flowing bottom-hole pressure.', { suffix: 'WellSolverFBHP' }),
  col('fwht', 'temperature', 'Solver-matched flowing well-head temperature.', { suffix: 'WellSolverFWHT' }),
  col('whp', 'pressureAbsolute', 'Solver-matched well-head pressure.', { suffix: 'WellSolverWHP' }),
  col('gor', 'gasOilRatio', 'Solver-matched gas-oil ratio.', { suffix: 'WellSolverGor' }),
  col('waterCut', 'fraction', 'Solver-matched water cut.', { suffix: 'WellSolverWaterCut' }),
];

/**
 * Well-bore operating pressures — broadly reused. Base flowing pressures
 * (fbhp/fwhp) compose with bottom-hole/well-head/temperature/drawdown
 * conditions where the source exposes them.
 */
export const WELL_PRESSURES: Shape = [
  col('fbhp', 'pressureAbsolute', 'Flowing bottom-hole pressure.', { suffix: 'FBHP' }),
  col('fwhp', 'pressureAbsolute', 'Flowing well-head pressure.', { suffix: 'FWHP' }),
  col('bhp', 'pressureAbsolute', 'Well bottom-hole pressure.', { suffix: 'WellBHP' }),
  col('whp', 'pressureAbsolute', 'Well-head pressure.', { suffix: 'Whp' }),
  col('wht', 'temperature', 'Well-head temperature.', { suffix: 'Wht' }),
  col('drawdown', 'pressureDelta', 'Reservoir-to-bottom-hole pressure drawdown.', { suffix: 'Drawdown' }),
];
/**
 * Network-solve calculated rates, pressures, injection and match-index outputs
 * carried by `NetworkOutputs_String_Agent`. Unlike the `nmv`-prefixed
 * StringOutputs family, this source's measure columns are not produced by a
 * single uniform prefix: the oil/gas/water rates de-prefix via
 * `prefix:'calculatedWell'` (reusing `PHASE_RATES`), but the remaining members
 * carry verbatim `calculatedWell*` / `calculated*` / bare names with no shared
 * tail, so they are authored here with `prefix:''` (suffix === source column).
 */
export const NET_STRING_CALC_EXTRAS: Shape = [
  col('liquidRate', 'liquidRate', 'Network-solved total liquid rate.', { suffix: 'calculatedWellLiquidRate' }),
  col('fbhp', 'pressureAbsolute', 'Network-calculated flowing bottom-hole pressure.', { suffix: 'calculatedWellFBHP' }),
  col('fwhp', 'pressureAbsolute', 'Network-calculated flowing well-head pressure.', { suffix: 'calculatedWellFWHP' }),
  col('bhp', 'pressureAbsolute', 'Network-calculated bottom-hole pressure.', { suffix: 'calculatedWellBHP' }),
  col('bhpGI', 'pressureAbsolute', 'Gas-injection bottom-hole pressure.', { suffix: 'calculatedWellBHPGI' }),
  col('bhpWI', 'pressureAbsolute', 'Water-injection bottom-hole pressure.', { suffix: 'calculatedWellBHPWI' }),
  col('gasRateGI', 'gasRate', 'Gas-injection rate.', { suffix: 'calculatedWellGasRateGI' }),
  col('waterRateWI', 'liquidRate', 'Water-injection rate.', { suffix: 'calculatedWellWaterRateWI' }),
  col('espMotorLoad', 'fraction', 'ESP motor load from the network solve.', { suffix: 'calculatedWellESPMotorLoad' }),
  col('chokeBeanSize', 'chokeSize', 'Calculated choke bean size.', { suffix: 'calculatedChokeBeanSize' }),
  col('chokeOpening', 'fraction', 'Calculated choke opening.', { suffix: 'calculatedChokeOpening' }),
  col('espFrequency', 'frequency', 'Calculated ESP frequency.', { suffix: 'calculatedEspFrequency' }),
  col('gasRateMatchIndex', 'unitless', 'Gas-rate match index, calculated vs measured.', {
    suffix: 'calculatedGasRateMatchIndex',
  }),
  col('oilRateMatchIndex', 'unitless', 'Oil-rate match index, calculated vs measured.', {
    suffix: 'calculatedOilRateMatchIndex',
  }),
  col('waterRateMatchIndex', 'unitless', 'Water-rate match index, calculated vs measured.', {
    suffix: 'calculatedWaterRateMatchIndex',
  }),
  col('power', 'power', 'Calculated power from the network solve.', { suffix: 'calculatedPower' }),
  // Source typo preserved verbatim ("Lquid"): do NOT "fix" the suffix.
  col('pumpLiquidRate', 'liquidRate', 'Pump liquid rate from the network solve.', {
    suffix: 'calculatedPumpLquidRate',
  }),
  col('espEfficiency', 'fraction', 'ESP efficiency from the network solve.'),
  col('formationGOR', 'gasOilRatio', 'Formation gas-oil ratio input to the network model.'),
  col('chokeDP', 'pressureDelta', 'Choke pressure drop from the network solve.'),
];

/**
 * Per-string flow-assurance + operating-constraint condition flags on
 * `NetworkOutputs_String_Agent`. The source names lead lowercase and carry a
 * trailing `_well` suffix (`asphalteneFlag_well`, …) that `prefix + suffix`
 * cannot reproduce off the base `FLOW_ASSURANCE_FLAGS` (PascalCase, no `_well`),
 * so they are authored verbatim with `prefix:''`.
 */
export const NET_STRING_FLOW_ASSURANCE_FLAGS: Shape = [
  col('asphalteneFlag', 'unitless', 'Asphaltene deposition risk flag at the well.', { suffix: 'asphalteneFlag_well' }),
  col('hydrateFlag', 'unitless', 'Hydrate formation risk flag at the well.', { suffix: 'hydrateFlag_well' }),
  col('scaleFlag', 'unitless', 'Scale deposition risk flag at the well.', { suffix: 'scaleFlag_well' }),
  col('waxFlag', 'unitless', 'Wax deposition risk flag at the well.', { suffix: 'waxFlag_well' }),
  col('pe5StabilityFlag', 'unitless', 'PE5 flow-stability flag at the well.', { suffix: 'pe5StabilityFlag_well' }),
  col('constraintFlag', 'unitless', 'Operating-constraint flag at the well.', { suffix: 'constraintFlag_well' }),
];

/**
 * Oil-only well-solver match triplet on `NetworkOutputs_String_Agent`:
 * measured / estimated / difference. The full `WELL_SOLVER_MATCH` shape spans
 * every phase plus solver pressures/GOR/water-cut, none of which exist here, so
 * this source carries only the oil triplet, authored verbatim with `prefix:''`.
 */
export const NET_STRING_WELL_SOLVER_OIL: Shape = [
  col('oilEstimated', 'liquidRate', 'Well-solver estimated oil rate input.', { suffix: 'wellSolverEstimatedOilRate' }),
  col('oilMeasured', 'liquidRate', 'Well-solver measured oil rate input.', { suffix: 'wellSolverMeasuredOilRate' }),
  col('oilDifference', 'liquidRate', 'Well-solver measured-vs-estimated oil-rate difference.', {
    suffix: 'wellSolverDifference',
  }),
];

/**
 * Gas-lift injection outputs on `NetworkOutputs_String_Agent`. The source
 * exposes only injection rate/pressure plus the calculated gas-lift rate —
 * none of the `Gl*` design columns of `GAS_LIFT_OUTPUTS` exist here — so these
 * are authored verbatim with `prefix:''`.
 */
export const NET_STRING_GAS_LIFT: Shape = [
  col('injection', 'gasRate', 'Gas-lift injection rate from the network solve.', { suffix: 'gasLiftInjection' }),
  col('injectionPressure', 'pressureAbsolute', 'Gas-lift injection pressure.', { suffix: 'gasLiftInjectionPress' }),
  col('gasLiftRate', 'gasRate', 'Calculated gas-lift rate from the network solve.', {
    suffix: 'calculatedGasLiftRate',
  }),
];

/**
 * Standard-conditions oil/gas/water/liquid production rates. Distinct from
 * `PHASE_RATES`: the `EstimatedRates_String_Agent` per-method family carries a
 * `…RateStd` (standard-conditions) tail and includes the liquid phase, which
 * `PHASE_RATES` (three phases, no `Std`) lacks — so it is authored separately
 * rather than reusing/extending the base.
 */
